import humps from 'humps'
import * as R from 'ramda'
import React from 'react'
import { DropdownOptionText } from '../QuestionConditionModal/DropdownOptionText'

import constants from 'config/constants'
import { TEMP_ID_PREFIX, TEMP_ID_PRIMARY_PREFIX } from 'organisms/SurveySettingsV2/constants'

const DEFAULT_SENTIMENT_MAP = {
  1: 'negative',
  2: 'negative',
  3: 'neutral',
  4: 'positive',
  5: 'positive',
}

export function prepareMainQuestionAndFollowUpQuestion(questionList) {
  const questionTypeToKey = {
    follow_up_multiple_choice_question: 'followUpMultipleChoiceQuestion',
    follow_up_multi_select_choice_question: 'followUpMultiSelectChoiceQuestion',
    follow_up_comment_question: 'followUpCommentQuestion',
    follow_up_file_upload_question: 'followUpFileUploadQuestion',
  }

  const checkFollowUpQuestionType = (conditionalLogicType) => {
    if (conditionalLogicType === 'followUpMultipleChoiceQuestion') {
      return 'multipleChoice'
    } else if (conditionalLogicType === 'followUpMultiSelectChoiceQuestion') {
      return 'multi_select_choice'
    } else if (conditionalLogicType === 'followUpFileUploadQuestion') {
      return 'file_upload'
    }
    return 'comment'
  }

  const mapQuestionToOptions = (question) => {
    const isCsatMetric = question.metric?.toUpperCase?.() === 'CSAT'
    const formChoices = question.choices || []
    const conditionChoices = question.questionCondition?.choices || []

    if (question.type === 'slider') {
      let choices =
        question.metric === 'CUSTOM' ? question.choices : question.questionCondition?.choices || []

      choices = choices.map((item, index) => {
        if (index === 0) {
          return { ...item, label: question.leftSideLabel }
        } else if (index === choices.length - 1) {
          return { ...item, label: question.rightSideLabel }
        }
        return item
      })

      const questionOption = {
        id: question.id || question.uniqueIdentifier,
        key: question.id,
        text: question.text,
        value: question.id,
        order: question.order,
        type: question.type,
        choices: choices,
        is_opinion_scale: question.isOpinionScale,
      }

      const camelizedQuestion = humps.camelizeKeys(question)
      const followUpOptions = (camelizedQuestion.questionCondition?.conditions || [])
        .map((condition) => {
          const followUpKey = questionTypeToKey[condition.conditionalQuestionType]
          const followUp = condition[followUpKey]
          const followUpType = checkFollowUpQuestionType(followUpKey)
          return followUp?.id
            ? {
                id: followUp.id,
                key: followUp.id,
                text: followUp.text,
                value: followUp.id,
                order: question.order,
                type: followUpType,
                choices: followUp.choices || [],
                is_opinion_scale: followUp.isOpinionScale,
              }
            : null
        })
        .filter(Boolean)

      return [questionOption, ...followUpOptions]
    }

    if (question.metric === '5-STAR') {
      const allFiveStarChoices = [1, 2, 3, 4, 5].map((ratingValue) => {
        const existingChoice =
          formChoices.find((choice) => String(choice.value) === String(ratingValue)) ||
          conditionChoices.find((choice) => String(choice.value) === String(ratingValue)) ||
          {}

        let label = existingChoice.label || ''
        if (!label) {
          if (ratingValue === 1) {
            label = question.leftSideLabel || ''
          } else if (ratingValue === 5) {
            label = question.rightSideLabel || ''
          }
        }

        return {
          id: existingChoice.id ?? `${TEMP_ID_PRIMARY_PREFIX}${ratingValue}`,
          value: String(ratingValue),
          label,
          sentiment: existingChoice.sentiment || DEFAULT_SENTIMENT_MAP[ratingValue],
        }
      })

      const mainQuestionOption = {
        id: question.id || question.uniqueIdentifier,
        key: question.id,
        text: question.text,
        value: question.id,
        order: question.order,
        type: question.type,
        choices: allFiveStarChoices,
        is_opinion_scale: question.isOpinionScale,
      }

      const camelizedQuestion = humps.camelizeKeys(question)
      const followUpQuestions = (camelizedQuestion.questionCondition?.conditions || [])
        .map((condition) => {
          const followUpKey = questionTypeToKey[condition.conditionalQuestionType]
          const followUp = condition[followUpKey]
          return followUp?.id
            ? {
                id: followUp.id,
                key: followUp.id,
                text: followUp.text,
                value: followUp.id,
                order: question.order,
                type: followUp.type,
                choices: [...(followUp.choices || [])],
                is_opinion_scale: followUp.isOpinionScale,
              }
            : null
        })
        .filter(Boolean)

      return [mainQuestionOption, ...followUpQuestions]
    }

    if (isCsatMetric) {
      const currentScale = formChoices.length || 5
      const includedRatingValues = constants.SCALE_RATING[currentScale]

      const allPossibleRatings = [1, 2, 3, 4, 5].map((ratingValue) => {
        const existingChoice =
          formChoices.find((choice) => String(choice.value) === String(ratingValue)) ||
          conditionChoices.find((choice) => String(choice.value) === String(ratingValue)) ||
          {}

        return {
          id: existingChoice.id ?? `${TEMP_ID_PRIMARY_PREFIX}${ratingValue}`,
          value: String(ratingValue),
          label: existingChoice.label || constants.DEFAULT_CSAT_LABEL_STATE[ratingValue] || '',
          sentiment: existingChoice.sentiment || DEFAULT_SENTIMENT_MAP[ratingValue],
        }
      })

      const filteredRatingChoices = includedRatingValues
        .map((rating) => allPossibleRatings.find((choice) => Number(choice.value) === rating))
        .filter(Boolean)

      const mainQuestionOption = {
        id: question.id || question.uniqueIdentifier,
        key: question.id,
        text: question.text,
        value: question.id,
        order: question.order,
        type: question.type,
        choices: filteredRatingChoices,
        is_opinion_scale: question.isOpinionScale,
      }

      const camelizedQuestion = humps.camelizeKeys(question)
      const followUpQuestions = (camelizedQuestion.questionCondition?.conditions || [])
        .map((condition) => {
          const conditionTypeMap = {
            follow_up_multiple_choice_question: 'followUpMultipleChoiceQuestion',
            follow_up_multi_select_choice_question: 'followUpMultiSelectChoiceQuestion',
            follow_up_comment_question: 'followUpCommentQuestion',
            follow_up_file_upload_question: 'followUpFileUploadQuestion',
          }
          const followUpKey = conditionTypeMap[condition.conditionalQuestionType]
          const followUpQuestion = condition[followUpKey]

          return followUpQuestion?.id
            ? {
                id: followUpQuestion.id,
                key: followUpQuestion.id,
                text: followUpQuestion.text,
                value: followUpQuestion.id,
                order: question.order,
                type: followUpQuestion.type,
                choices: [...(followUpQuestion.choices || [])],
                is_opinion_scale: followUpQuestion.isOpinionScale,
              }
            : null
        })
        .filter(Boolean)

      return [mainQuestionOption, ...followUpQuestions]
    }

    const mergedChoiceList = (formChoices || []).map((choice, index) => {
      const conditionChoice = conditionChoices[index] || {}
      return {
        id: choice.id ?? conditionChoice.id ?? `${TEMP_ID_PREFIX}${index}`,
        value: String(choice.value ?? conditionChoice.value ?? index + 1),
        label: choice.label || conditionChoice.label || '',
        sentiment: choice.sentiment || conditionChoice.sentiment,
      }
    })

    const orderedChoiceList = mergedChoiceList.sort((a, b) => Number(a.value) - Number(b.value))

    const questionOption = {
      id: question.id || question.uniqueIdentifier,
      key: question.id,
      text: question.text,
      value: question.id,
      order: question.order,
      type: question.type,
      choices: orderedChoiceList,
      is_opinion_scale: question.isOpinionScale,
    }

    return [questionOption]
  }

  return questionList.flatMap(mapQuestionToOptions)
}

export function getQuestionChoiceDropdownOptions(question) {
  if (!question) {
    return []
  }

  const { choices, type } = question
  const isOpinionScale = R.prop('is_opinion_scale', question)

  const sentimentOptions = [
    {
      key: 'positive',
      text: 'Positive',
      content: <span>😍 Positive</span>,
      value: 'positive',
      color: 'green',
      sentiment: 'positive',
    },
    {
      key: 'neutral',
      text: 'Neutral',
      content: <span>😐 Neutral</span>,
      value: 'neutral',
      color: 'orange',
      sentiment: 'neutral',
    },
    {
      key: 'negative',
      text: 'Negative',
      content: <span>😠 Negative</span>,
      value: 'negative',
      color: 'red',
      sentiment: 'negative',
    },
  ]

  const ordered = [...(choices || [])].sort((a, b) => Number(a.value) - Number(b.value))
  const orderedChoices = isOpinionScale ? ordered.reverse() : ordered

  const choiceOptions = orderedChoices
    .filter((choice) => {
      if (type === 'star') {
        return true
      }
      const safeLabel = getSafeLabel(choice)
      return safeLabel !== ''
    })
    .map((choice) => {
      const safeLabel = getSafeLabel(choice)

      return {
        key: choice.id,
        text: safeLabel,
        content: (
          <DropdownOptionText choice={choice} questionType={type} isOpinionScale={isOpinionScale} />
        ),
        value: choice.id,
        color: null,
        choice,
      }
    })

  if (isOpinionScale) {
    const validSentiments = new Set(
      (choices || []).map((choice) => choice.sentiment).filter(Boolean)
    )
    const visibleSentiments = sentimentOptions.filter((o) => validSentiments.has(o.sentiment))
    return [...visibleSentiments, ...choiceOptions]
  }

  return choiceOptions
}

export function getQuestionLabel(type, text) {
  if (type === 'multipleChoice' || type === 'star') {
    return (
      <span>
        <i className="icon green fas fa-list" /> {text}
      </span>
    )
  }
  if (type === 'multi_select_choice') {
    return (
      <span>
        <i className="icon blue fas fa-list-check" /> {text}
      </span>
    )
  }
  if (type === 'slider' || type === 'rating_scale_minmax') {
    return (
      <span>
        <i className="icon yellow fas fa-square-sliders" /> {text}
      </span>
    )
  }
  if (type === 'file_upload') {
    return (
      <span>
        <i className="icon violet far fa-paperclip" /> {text}
      </span>
    )
  }
  return <span>{text}</span>
}

export function getSafeLabel(choice) {
  return choice?.label?.trim?.() || ''
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/QuestionConditionalLogic/utils.js